import { forwardRef, useState } from 'react';
import { Eye, EyeSlash } from '@phosphor-icons/react';
import { cn } from '../../utils/cn';
import { CALLER_FONT_SIZE } from './fieldFontSize';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Label text rendered above the input */
  label?: string;
  /** Error message -- turns border red and shows error text below */
  error?: string;
  /** Helper text shown below the input (hidden when error is present) */
  helperText?: string;
  /** Icon element rendered on the left inside the input */
  leadingIcon?: React.ReactNode;
  /** Icon element rendered on the right inside the input */
  trailingIcon?: React.ReactNode;
  /** Static suffix text rendered inside the input on the right (e.g. "MB", "tokens") */
  suffix?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  function Input(
    { label, error, helperText, leadingIcon, trailingIcon, suffix, className, id, type, ...props },
    ref,
  ) {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const resolvedType = isPassword && showPassword ? 'text' : type;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-text-secondary"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leadingIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary">
              {leadingIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            className={cn(
              'w-full px-3 py-2 rounded-lg text-text-primary bg-bg-secondary min-h-[var(--touch-target-min)] lg:min-h-0',
              // >= 16px below lg (no iOS zoom on focus), 14px from lg; a caller font size replaces both
              !CALLER_FONT_SIZE.test(className ?? '') && 'text-base lg:text-sm',
              'border border-border-control outline-none transition-all duration-200',
              'placeholder:text-text-tertiary',
              'focus:border-border-focus focus:ring-1 focus:ring-accent-500/30',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              error && 'border-border-error focus:border-border-error focus:ring-error/30',
              !!leadingIcon && 'pl-10',
              !!(trailingIcon || isPassword) && 'pr-10',
              !!suffix && !trailingIcon && !isPassword && 'pr-12',
              className,
            )}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary transition-colors cursor-pointer"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              tabIndex={-1}
            >
              {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
            </button>
          )}
          {trailingIcon && !isPassword && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary">
              {trailingIcon}
            </span>
          )}
          {suffix && !trailingIcon && !isPassword && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-tertiary pointer-events-none select-none">
              {suffix}
            </span>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="text-xs text-error-text" role="alert">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${inputId}-helper`} className="text-xs text-text-tertiary">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

export default Input;
